import { colorStrings, mediaQueriesBP } from "@iewcasia/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Img from "@insite/mobius/Img";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useEmblaCarousel from "embla-carousel-react";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    title = "title",
    cards = "cards",
    cardTitle = "cardTitle",
    cardImage = "cardImage",
    cardDescription = "cardDescription",
    cardLink = "cardLink",
    cardLinkLabel = "cardLinkLabel",
    openInNewWindow = "openInNewWindow",
}

interface CardModel {
    fields: any;
    [fields.cardTitle]: string;
    [fields.cardImage]: string;
    [fields.cardDescription]: string;
    [fields.cardLink]: LinkModel;
    [fields.cardLinkLabel]: string;
    [fields.openInNewWindow]: boolean;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.cards]: CardModel[];
    };
}

export type Props = OwnProps;

export interface TopPurchasedStyles {
    title?: TypographyProps;
    container?: GridContainerProps;
    purchasedItem?: GridItemProps;
    imageWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    slide?: InjectableCss;
    innerContainer?: InjectableCss;
    contentWrapper?: InjectableCss;
    cardTitle?: TypographyProps;
    cardDescription?: TypographyProps;
    cardLink?: LinkProps;
    prevArrowGridItem?: InjectableCss;
    prevArrowButton?: ButtonProps;
    nextArrowGridItem?: InjectableCss;
    nextArrowButton?: ButtonProps;
}

export const CardGroupStyles: TopPurchasedStyles = {
    title: {
        variant: "h2",
        css: css`
            margin-bottom: 1rem;
        `,
    },
    container: {
        gap: 0,
        css: css`
            width: 100%;

            & > div {
                flex-wrap: nowrap !important;
            }

            &.static {
                width: calc(100% + 16px);
                margin-left: -8px;
            }
        `,
    },

    carouselContainer: {
        css: css`
            width: 100%;
            overflow: hidden;
        `,
    },

    innerContainer: {
        css: css`
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            border: 1px solid ${getColor(colorStrings.commonGlobalGray03)};
            position: relative;
            padding: 0;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            max-width: unset;
            text-align: left;
            align-items: flex-start;
            height: 100%;
        `,
    },

    imageWrapper: {
        css: css`
            width: 100%;
            img {
                width: 100%;
                height: auto;
                aspect-ratio: 4/3;
                object-fit: cover;
                cursor: pointer;
                border-bottom: 5px solid ${getColor(colorStrings.secondaryMain)};
            }
        `,
    },

    contentWrapper: {
        css: css`
            padding: 16px;
            flex-direction: column;
            display: flex;
            width: 100%;
            height: 100%;

            ${mediaQueriesBP.maxLG} {
                padding: 8px;
            }
        `,
    },

    cardTitle: {
        variant: "p",
        as: "h3",
        size: 18,
        weight: 600,
        css: css`
            margin-bottom: 8px;
            cursor: pointer;
        `,
    },

    cardDescription: {
        size: 14,
        css: css`
            margin-bottom: 8px;
            display: block;
            /* stylelint-disable-next-line */
            display: -webkit-box;
            /* stylelint-disable-next-line */
            -webkit-line-clamp: 4;
            /* stylelint-disable-next-line */
            -webkit-box-orient: vertical;
            overflow-y: hidden;
        `,
    },

    cardLink: {
        css: css`
            margin-top: auto;
        `,
    },

    prevArrowGridItem: {
        css: css`
            justify-content: flex-start;
            display: flex;
            align-items: center;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },

    nextArrowGridItem: {
        css: css`
            justify-content: flex-end;
            display: flex;
            align-items: center;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
};

export const styles = CardGroupStyles;

export const CarouselSlidesContainer = styled.div<InjectableCss>`
    display: flex;
    flex-direction: row;
    align-items: initial;
    height: 100%;
    ${injectCss};
`;

export const CarouselSlide = styled.div<InjectableCss>`
    flex: 0 0 25%;
    padding: 0;
    position: relative;
    height: 100%;
    padding-inline: 8px;
    ${injectCss};

    ${mediaQueriesBP.maxLG} {
        flex: 0 0 33.3333%;
    }

    ${mediaQueriesBP.maxMD} {
        flex: 0 0 50%;
    }

    ${mediaQueriesBP.maxSM} {
        flex: 0 0 100%;
    }
`;

export const CardGroupWidget = ({ fields }: Props) => {
    const cardLinks = useGetLinks(
        (fields.cards ?? []).map(card => card.fields.cardLink),
        o => o,
    );

    const [emblaRef, embla] = useEmblaCarousel({
        axis: "x",
    });
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
    }, [embla]);

    useEffect(() => {
        if (!embla || !fields.cards || fields.cards.length === 0) {
            return;
        }

        embla.reInit({
            align: fields.cards.length > 4 ? "start" : "center",
            slidesToScroll: 1,
            containScroll: "trimSnaps",
            loop: false,
            axis: "x",
        });
        setCanScroll();
    }, [embla, fields.cards]);

    const handleClick = (index: number) => {
        // redirect to the card at index link
        const link = (cardLinks.links[index]?.url as string) ?? "";
        const openInNewWindow = fields.cards[index].fields.openInNewWindow;
        window.open(link, openInNewWindow ? "_blank" : "_self");
    };

    if (!fields.cards || fields.cards.length === 0) {
        return null;
    }

    return (
        <>
            <Typography {...styles.title}>{fields.title}</Typography>
            <GridContainer {...styles.container} className={fields.cards.length <= 4 ? "static" : ""}>
                <StyledWrapper {...styles.prevArrowGridItem}>
                    {fields.cards.length > 4 && (
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                        >
                            <ButtonIcon src={ChevronLeft} />
                        </Button>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                    <CarouselSlidesContainer className="CarouselSlidesContainer">
                        {fields.cards.map((card: CardModel, index: number) => {
                            const link = (cardLinks.links[index]?.url as string) ?? "";

                            return (
                                <CarouselSlide {...styles.slide} key={index}>
                                    <StyledWrapper {...styles.innerContainer}>
                                        <StyledWrapper {...styles.imageWrapper}>
                                            <Img onClick={() => handleClick(index)} src={card.fields.cardImage} />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.contentWrapper}>
                                            <Typography onClick={() => handleClick(index)} {...styles.cardTitle}>
                                                {card.fields.cardTitle}
                                            </Typography>
                                            <Typography {...styles.cardDescription}>
                                                {card.fields.cardDescription}
                                            </Typography>
                                            {card.fields.cardLink.value && (
                                                <Link
                                                    target={card.fields.openInNewWindow ? "_blank" : ""}
                                                    {...styles.cardLink}
                                                    href={link}
                                                >
                                                    {card.fields.cardLinkLabel || translate("Read More")}
                                                </Link>
                                            )}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </CarouselSlide>
                            );
                        })}
                    </CarouselSlidesContainer>
                </StyledWrapper>
                <StyledWrapper {...styles.nextArrowGridItem}>
                    {fields.cards.length > 4 && (
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                        >
                            <ButtonIcon src={ChevronRight} />
                        </Button>
                    )}
                </StyledWrapper>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, null)(CardGroupWidget as any) as any,
    definition: {
        displayName: "Content Group Widget",
        group: "Verndale Box" as any,
        icon: "puzzle-piece",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 1,
                isRequired: false,
            },
            {
                name: fields.cards,
                displayName: "Cards",
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: ({ fields: { cardTitle } }) => cardTitle || "Card",
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: fields.cardImage,
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        sortOrder: 0,
                        isRequired: true,
                    },
                    {
                        name: fields.cardTitle,
                        displayName: "Card Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 1,
                        isRequired: true,
                    },
                    {
                        name: fields.cardDescription,
                        displayName: "Card Description",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 2,
                    },
                    {
                        name: fields.cardLinkLabel,
                        displayName: "Card Link Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 3,
                        isRequired: true,
                    },
                    {
                        name: fields.cardLink,
                        displayName: "Card Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        sortOrder: 4,
                        isRequired: true,
                    },
                    {
                        name: fields.openInNewWindow,
                        displayName: "Open in New Window",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        sortOrder: 5,
                    },
                ],
                sortOrder: 2,
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
